import { RefreshResponseSchema } from '@daddytime/api-contracts';

/**
 * Thrown when a token refresh cannot be completed — the endpoint returned a
 * non-OK status (refresh token expired/revoked → 401) or the body was
 * off-spec. Callers treat this as "the session is dead": clear auth and drop
 * to anonymous.
 */
export class RefreshError extends Error {
  constructor(
    message: string,
    readonly status?: number
  ) {
    super(message);
    this.name = 'RefreshError';
  }
}

export interface RefreshedTokens {
  accessToken: string;
  idToken: string;
}

/**
 * Exchange a Cognito refresh token for a fresh id/access token pair via
 * POST /auth/google/refresh.
 *
 * Pure and dependency-injected (fetch + base URL passed in) so it can be unit
 * tested without a DOM or network. Note the backend does not return a new
 * refresh token — the caller keeps the existing one.
 *
 * @throws RefreshError on non-OK status or an off-spec response body.
 */
export async function refreshIdToken(
  refreshToken: string,
  apiBaseUrl: URL,
  fetchImpl: typeof fetch = fetch
): Promise<RefreshedTokens> {
  const url = new URL('auth/google/refresh', apiBaseUrl);

  let response: Response;
  try {
    response = await fetchImpl(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
  } catch (err) {
    throw new RefreshError(
      `Token refresh request failed: ${(err as Error).message}`
    );
  }

  if (!response.ok) {
    throw new RefreshError(
      `Token refresh returned ${response.status}`,
      response.status
    );
  }

  const raw = await response.json().catch(() => null);
  const parsed = RefreshResponseSchema.safeParse(raw);
  if (!parsed.success) {
    throw new RefreshError('Token refresh response was off-spec');
  }

  return {
    accessToken: parsed.data.access_token,
    idToken: parsed.data.id_token,
  };
}

/**
 * Decode the `exp` (expiry, seconds since epoch) claim from a JWT without
 * verifying its signature — we only need the timestamp to decide when to
 * refresh, and the backend re-verifies on every request anyway. Returns null
 * if the token is malformed or has no exp claim.
 */
export function decodeJwtExp(token: string): number | null {
  const parts = token.split('.');
  const payloadSeg = parts[1];
  if (parts.length !== 3 || !payloadSeg) return null;
  try {
    const json = atob(payloadSeg.replace(/-/g, '+').replace(/_/g, '/'));
    const payload = JSON.parse(json) as { exp?: unknown };
    return typeof payload.exp === 'number' ? payload.exp : null;
  } catch {
    return null;
  }
}

/**
 * True if the id token is expired or will expire within `skewSeconds`. An
 * undecodable token is treated as expiring (fail safe → trigger a refresh
 * rather than send a token we can't reason about).
 *
 * @param nowMs injectable clock for tests; defaults to Date.now().
 */
export function isTokenExpiringSoon(
  token: string,
  skewSeconds: number,
  nowMs: number = Date.now()
): boolean {
  const exp = decodeJwtExp(token);
  if (exp === null) return true;
  return exp * 1000 <= nowMs + skewSeconds * 1000;
}
